import React, { useEffect, useState } from 'react'
import { Form, Formik } from 'formik'

import { Column, Row } from '@components/kit/grid'
import Box from '@components/kit/box'
import Text from '@components/kit/text'
import ScrollView from '@components/kit/scroll-view'

import { FormButtonPanel, TextFieldFormated } from '@components/formFields'
import { IOpportunityDetails } from '@store/Opportunity/types'
import { useOpportunityStore } from '@store/Opportunity'
import {
  getOpportunityDetailTitle,
  getOpportunityDetailValue,
  shouldRenderOpportunityDetail
} from '@utils/opportunity'
import { HomepageQuestionCard } from './homepage-question-card'
import Button from '@components/kit/button'
import Loading from '@components/kit/loading'

type QuestionCard = {
  text: string
  description: string
}

interface IEditOpportunityProps {
  ruleName: string
  onClose: () => void
}

export function EditOpportunity({ ruleName, onClose }: IEditOpportunityProps) {
  const { getOpportunitiesUniqueRulesByName, upsertOpportunityUniqueRule } =
    useOpportunityStore.getState()
  const [opportunity, setOpportunity] = useState<IOpportunityDetails | null>(
    null
  )
  const [questionCards, setQuestionCards] = useState<QuestionCard[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleChangeTitle = (index: number, title: string) => {
    setQuestionCards(prevCards => {
      const updatedCards = [...prevCards]
      updatedCards[index].text = title
      return updatedCards
    })
  }

  const handleChangeDescription = (index: number, description: string) => {
    setQuestionCards(prevCards => {
      const updatedCards = [...prevCards]
      updatedCards[index].description = description
      return updatedCards
    })
  }

  const isRemovableQuestionCard = (index: number) => {
    return questionCards.length > 1 && index < questionCards.length
  }

  const handleRemoveQuestionCard = (index: number) => {
    if (isRemovableQuestionCard(index)) {
      setQuestionCards(prevCards => prevCards.filter((_, i) => i !== index))
    } else {
      console.warn('At least one question card must remain.')
    }
  }

  const handleAddQuestionCard = () => {
    setQuestionCards(prevCards => [...prevCards, { text: '', description: '' }])
  }

  const handleSave = async () => {
    setIsLoading(true)
    upsertOpportunityUniqueRule(opportunity.rule_name, questionCards).then(
      success => {
        setIsLoading(false)
        if (success) {
          handleClose()
        }
      }
    )
  }

  const handleClose = () => {
    setQuestionCards([])
    setOpportunity(null)
    onClose()
  }

  useEffect(() => {
    const opportunityData = getOpportunitiesUniqueRulesByName(ruleName)
    if (!opportunityData) {
      console.error('Opportunity not found')
    }
    setOpportunity(opportunityData)
  }, [ruleName, getOpportunitiesUniqueRulesByName])

  useEffect(() => {
    if (opportunity?.questions) {
      if (!opportunity?.questions?.length) {
        setQuestionCards([{ text: '', description: '' }])
      } else {
        setQuestionCards(opportunity.questions)
      }
    }
  }, [opportunity])

  useEffect(() => {
    if (!!opportunity && opportunity?.rule_name !== ruleName) {
      setOpportunity(null)
      setQuestionCards([])
    }
  }, [ruleName, opportunity?.rule_name])

  return (
    <Box>
      <Formik initialValues={{}} onSubmit={() => {}} validateOnChange={true}>
        <Form>
          <Box paddingTop='16' width='80vw' maxHeight='80vh'>
            <Text
              font='display.20.bold'
              textColor='walrusBlue200'
              marginBottom='32'
            >
              Opportunity
            </Text>
            <Box width='100%' flex='1'>
              <Row width='100%' flex='1'>
                <Column
                  flex='1'
                  width='50%'
                  borderRightWidth='2px'
                  borderColor='walrusBlue050'
                  paddingRight='24'
                >
                  <TextFieldFormated
                    name='Rule Name'
                    label='Name'
                    value={opportunity?.rule_name || ''}
                    onChangeText={() => {}}
                  />
                  <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ flexShrink: 1 }}
                    showsVerticalScrollIndicator={false}
                    marginTop='24'
                  >
                    <Box>
                      <Text
                        font='display.14.bold'
                        textColor='walrusBlue200'
                        marginBottom='08'
                      >
                        Example
                      </Text>
                      {!!opportunity &&
                        Object.keys(opportunity)?.map((key, index) => {
                          if (!shouldRenderOpportunityDetail(key)) {
                            return null
                          }
                          return (
                            <Box flexDirection='row' marginTop='08'>
                              <Text
                                key={index}
                                font='display.12.bold'
                                textColor='walrusNeutral700'
                                textTransform='capitalize'
                              >
                                {`${getOpportunityDetailTitle(key)}:`}
                              </Text>
                              <Text
                                font='text.12.medium'
                                textColor='walrusNeutral700'
                              >
                                {` ${getOpportunityDetailValue(
                                  key,
                                  opportunity[key]
                                )}`}
                              </Text>
                            </Box>
                          )
                        })}
                    </Box>
                  </ScrollView>
                </Column>
                <Column flex='1' width='50%' marginLeft='24'>
                  <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ flexShrink: 1 }}
                    showsVerticalScrollIndicator={false}
                  >
                    <Box width='100%' paddingRight='24'>
                      <Text
                        font='display.16.bold'
                        textColor='walrusBlue200'
                        marginBottom='24'
                      >
                        Suggestions
                      </Text>
                      {questionCards.map((card, index) => (
                        <Box key={index} marginBottom='16' width='100%'>
                          <HomepageQuestionCard
                            title={card.text}
                            description={card.description}
                            onChangeTitle={(title: string) =>
                              handleChangeTitle(index, title)
                            }
                            onChangeDescription={(description: string) =>
                              handleChangeDescription(index, description)
                            }
                            onRemove={() => handleRemoveQuestionCard(index)}
                            isRemovable={isRemovableQuestionCard(index)}
                          />
                        </Box>
                      ))}
                    </Box>
                    <Box
                      width='100%'
                      flexDirection='row'
                      justifyContent='flex-start'
                      marginTop='16'
                    >
                      <Button
                        variant='punchThroughPurple'
                        onPress={handleAddQuestionCard}
                        size='small'
                      >
                        + Add a Question Card
                      </Button>
                    </Box>
                  </ScrollView>
                </Column>
              </Row>
              <Box width='100%' flexDirection='row' justifyContent='flex-end'>
                <FormButtonPanel
                  onCancel={handleClose}
                  onSave={handleSave}
                  fullWidth={false}
                />
              </Box>
            </Box>
          </Box>
        </Form>
      </Formik>
      {isLoading && (
        <Box
          position='absolute'
          width='100%'
          height='100%'
          top='0'
          left='0'
          right='0'
          flex='1'
          justifyContent='center'
          alignItems='center'
          style={{
            backgroundColor: '#f4f7facc'
          }}
        >
          <Loading size='large' />
        </Box>
      )}
    </Box>
  )
}
